import React from 'react';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';

interface ScanningOverlayProps {
  isCalculating: boolean;
  message?: string;
}

const ScanningOverlay: React.FC<ScanningOverlayProps> = ({ isCalculating, message = "ANALYSING TRAJECTORIES..." }) => {
  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 5,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 243, 255, 0.05)',
  };

  const scanLineStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: 'linear-gradient(180deg, transparent 0%, #00f3ff 50%, transparent 100%)',
    boxShadow: '0 0 20px #00f3ff',
    opacity: 0.6,
    animation: 'scan-vertical 3s ease-in-out infinite',
  };

  const gridStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'linear-gradient(rgba(0, 243, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 243, 255, 0.1) 1px, transparent 1px)',
    backgroundSize: '40px 40px',
    opacity: 0.3,
  };

  const messageBoxStyle: React.CSSProperties = {
    padding: '24px',
    backgroundColor: 'rgba(8, 8, 12, 0.8)',
    border: '2px solid #00f3ff',
    boxShadow: '0 0 30px rgba(0, 243, 255, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    zIndex: 6,
    pointerEvents: 'auto',
  };

  return (
    <Fade in={isCalculating} timeout={500}>
      <div style={containerStyle}>
        <style>
          {`
            @keyframes scan-vertical {
              0% { top: 0%; }
              50% { top: 100%; }
              100% { top: 0%; }
            }
            @keyframes pulse-text {
              0% { opacity: 0.6; }
              50% { opacity: 1; }
              100% { opacity: 0.6; }
            }
            @keyframes loading-bar {
              0% { left: -40%; }
              100% { left: 100%; }
            }
          `}
        </style>
        
        {/* Horizontal Scanning Line */}
        <div style={scanLineStyle} />

        {/* Global Grid Overlay (Subtle) */}
        <div style={gridStyle} />

        {/* Processing Message */}
        <div style={messageBoxStyle}>
          <Typography variant="h5" sx={{ 
            color: '#00f3ff', 
            fontFamily: 'Orbitron', 
            fontWeight: 700, 
            letterSpacing: '0.2em',
            animation: 'pulse-text 1.5s infinite',
          }}>
            {message}
          </Typography>
          <div style={{ 
            width: '200px', 
            height: '2px', 
            backgroundColor: 'rgba(0, 243, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '40%',
              backgroundColor: '#00f3ff',
              boxShadow: '0 0 10px #00f3ff',
              animation: 'loading-bar 1s linear infinite',
            }} />
          </div>
          <Typography variant="caption" sx={{ color: 'rgba(0, 243, 255, 0.6)', fontFamily: 'monospace' }}>
            SYSTEM RESOURCE ALLOCATION: OPTIMIZED
          </Typography>
        </div>
      </div>
    </Fade>
  );
};

export default ScanningOverlay;
